/**
 * <s-sticky-buy> — mobile sticky add-to-cart bar.
 *
 * Lives inside the product form (its button is a regular `name="add"` submit button, so
 * <s-product-form> handles the AJAX add). The bar shows (data-visible) only when the element
 * referenced by `data-sentinel` (placed right after the main add-to-cart button) is above the
 * viewport, and hides while the page <footer> is on screen. Positions are re-read on scroll and
 * resize (one rAF per frame) rather than with IntersectionObserver, so instant jumps such as the
 * iOS "tap status bar to scroll to top" never leave the bar in a stale state.
 */
class SStickyBuy extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    const schedule = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    };
    window.addEventListener('scroll', schedule, opts);
    window.addEventListener('resize', schedule, opts);
    window.addEventListener('pageshow', schedule, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #update() {
    const sentinel = document.getElementById(this.dataset.sentinel || '');
    const footer = document.querySelector('footer');
    if (!sentinel) return;
    const pastButton = sentinel.getBoundingClientRect().bottom < 0;
    const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
    const visible = pastButton && !footerVisible;
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
  }
}

if (!customElements.get('s-sticky-buy')) {
  customElements.define('s-sticky-buy', SStickyBuy);
}
